import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, BookOpen, Calendar, Download, ExternalLink, FileText, Globe, Library } from "lucide-react";
import { api } from "../../lib/api";
import { publicAssetUrl } from "../../lib/adminApi";

const fallbackIssues = [
  { id: "v3n2-2025", title: "Vol. 3, No. 2 (November 2025)", slug: "vol-3-no-2-november-2025", volume: "Vol. 3", issue: "No. 2", publication_date: "2025-11-01", issn: "2786-9822", eissn: "3043-5382", publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria", description: "A multidisciplinary collection of peer-reviewed research papers from humanities, science and technology.", cover_image: "/journal-covers/vol3-no2-nov-2025.jpeg", pdf_url: "/journals/BIDA POLYMATH Vol 3 Issue 2 Nov 2025.pdf", articles: [] },
  { id: "v3n1-2025", title: "Vol. 3, No. 1 (June 2025)", slug: "vol-3-no-1-june-2025", volume: "Vol. 3", issue: "No. 1", publication_date: "2025-06-01", issn: "2786-9822", eissn: "3043-5382", publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria", description: "Latest research issue containing scholarly papers and innovation-focused academic contributions.", cover_image: "/journal-covers/vol3-no1-june-2025.jpeg", pdf_url: "/journals/BIDA POLYMATH Vol 3 Issue 1 June 2025.pdf", articles: [] },
  { id: "v2n2-2024", title: "Vol. 2, No. 2 (December 2024)", slug: "vol-2-no-2-december-2024", volume: "Vol. 2", issue: "No. 2", publication_date: "2024-12-01", issn: "2786-9822", eissn: "3043-5382", publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria", description: "Research papers and journal contributions from institutional academic authors.", cover_image: "/journal-covers/vol2-no2-dec-2024.jpeg", pdf_url: "/journals/BIDA VOLUME 2, NUMBER 2, DECEMBER 2024.pdf", articles: [] },
  { id: "v2n1-2024", title: "Vol. 2, No. 1 (June 2024)", slug: "vol-2-no-1-june-2024", volume: "Vol. 2", issue: "No. 1", publication_date: "2024-06-01", issn: "2786-9822", eissn: "3043-5382", publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria", description: "Open access issue featuring multidisciplinary research and academic scholarship.", cover_image: "/journal-covers/vol2-no1-june-2024.jpeg", pdf_url: "/journals/BIDA VOLUME 2, NUMBER 1, JUNE 2024.pdf", articles: [] },
];

function formatDate(value) {
  if (!value) return "Recently published";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
}

function resolveAsset(path) {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  if (path.startsWith("/journal-covers") || path.startsWith("/journals") || path.startsWith("/images")) return path;
  return publicAssetUrl(path);
}

function issuePdf(issue) {
  return issue?.pdf_url || issue?.issue_pdf || issue?.download_url || "";
}

export default function JournalIssueDetail() {
  const { slug } = useParams();
  const [issue, setIssue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError("");
      try {
        const data = await api(`/api/journal-issues/${slug}`);
        setIssue(data);
      } catch (err) {
        const fallback = fallbackIssues.find((item) => item.slug === slug || item.id === slug);
        if (fallback) {
          setIssue(fallback);
          setError("");
        } else {
          setError(err.message);
          setIssue(null);
        }
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  if (loading) return <main className="min-h-screen bg-slate-50 p-8">Loading journal issue...</main>;
  if (error || !issue) {
    return (
      <main className="min-h-screen bg-slate-50 p-8">
        <div className="mx-auto max-w-3xl rounded-3xl bg-white p-8 shadow-sm">
          <h1 className="text-2xl font-black text-slate-950">Issue not found</h1>
          <p className="mt-3 text-slate-600">{error || "The journal issue could not be loaded."}</p>
          <Link to="/polymath-journal" className="mt-5 inline-block font-black text-blue-700">Back to journal portal</Link>
        </div>
      </main>
    );
  }

  const cover = resolveAsset(issue.cover_image || issue.coverUrl);
  const pdf = issuePdf(issue);
  const articles = issue.articles || [];

  return (
    <main className="min-h-screen bg-[#f5f7fb] text-slate-950">
      <section className="relative overflow-hidden bg-[#101936] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(59,130,246,0.2),transparent_35%),linear-gradient(135deg,#08111f,#11183a_45%,#1d1b4f)]" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="relative mx-auto grid max-w-7xl gap-10 px-4 py-14 md:grid-cols-[320px_1fr] md:items-center md:px-6 md:py-20">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="overflow-hidden rounded-[2rem] border border-white/15 bg-white/10 p-3 shadow-2xl backdrop-blur">
            {cover ? (
              <img src={cover} alt={issue.title} className="h-[380px] w-full rounded-[1.5rem] object-cover" />
            ) : (
              <div className="flex h-[380px] items-center justify-center rounded-[1.5rem] bg-blue-950 p-8 text-center text-2xl font-black">Journal Issue</div>
            )}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}>
            <Link to="/polymath-journal" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm font-black text-blue-100 backdrop-blur">
              <ArrowLeft className="h-4 w-4" /> Back to journal portal
            </Link>
            <p className="mt-8 text-sm font-black uppercase tracking-[0.35em] text-blue-200">Polymath Journal Issue</p>
            <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight tracking-tight md:text-6xl">{issue.title}</h1>
            <p className="mt-5 text-lg font-semibold text-slate-200">
              {issue.volume || "Volume"} • {issue.issue || "Issue"} • {formatDate(issue.publication_date)}
            </p>
            <p className="mt-5 max-w-3xl text-base leading-8 text-slate-300">{issue.description || "Published research issue with article abstracts and downloadable papers."}</p>

            <div className="mt-7 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                <Calendar className="mb-2 h-5 w-5 text-blue-300" />
                <p className="text-xs font-bold text-slate-400">Published</p>
                <p className="font-black">{formatDate(issue.publication_date)}</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                <BookOpen className="mb-2 h-5 w-5 text-blue-300" />
                <p className="text-xs font-bold text-slate-400">Articles</p>
                <p className="font-black">{articles.length || issue.article_count || 0}</p>
              </div>
              <div className="rounded-2xl border border-white/15 bg-white/10 p-4 backdrop-blur">
                <Globe className="mb-2 h-5 w-5 text-blue-300" />
                <p className="text-xs font-bold text-slate-400">ISSN</p>
                <p className="font-black">{issue.issn || "2786-9822"}</p>
              </div>
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              {pdf ? (
                <a href={resolveAsset(pdf)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-blue-600 px-6 py-4 text-sm font-black text-white shadow-lg shadow-blue-950/20 hover:bg-blue-500">
                  Download Issue PDF <Download className="h-4 w-4" />
                </a>
              ) : null}
              <a href="#articles" className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-6 py-4 text-sm font-black text-slate-950 hover:bg-blue-50">
                View Articles <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <section id="articles" className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <div className="mb-7 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-xs font-black uppercase tracking-[0.2em] text-blue-700">
              <Library className="h-4 w-4" /> Issue Contents
            </p>
            <h2 className="mt-4 text-3xl font-black text-slate-950">Published Articles</h2>
          </div>
          <p className="text-sm font-black text-slate-500">{articles.length || 0} article(s)</p>
        </div>

        <div className="grid gap-5">
          {articles.map((article) => (
            <Link key={article.id} to={`/journals/article/${article.slug}`} className="block rounded-[1.5rem] border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:border-blue-200 hover:shadow-xl hover:shadow-blue-950/10">
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div>
                  <h3 className="text-xl font-black text-slate-950">{article.title}</h3>
                  <p className="mt-2 text-sm font-semibold text-slate-600">{article.authors}</p>
                  <p className="mt-3 line-clamp-3 text-sm leading-6 text-slate-600">{article.abstract}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {article.pages && <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">Pages: {article.pages}</span>}
                    {article.doi && <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-bold text-blue-700">DOI: {article.doi}</span>}
                    {Number(article.is_featured) === 1 && <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-bold text-yellow-800">Featured</span>}
                  </div>
                </div>
                <span className="shrink-0 rounded-2xl bg-blue-700 px-5 py-3 text-center text-sm font-black text-white">View Article</span>
              </div>
            </Link>
          ))}

          {articles.length === 0 ? (
            <div className="rounded-[1.5rem] border border-slate-200 bg-white p-8 text-center text-slate-600 shadow-sm">
              <FileText className="mx-auto mb-3 h-10 w-10 text-blue-600" />
              Articles have not been added to this issue yet. Add article records from the admin Journal Articles CMS.
            </div>
          ) : null}
        </div>
      </section>
    </main>
  );
}
